import {Button, Form, InputNumber, Modal, Space, Tooltip} from "antd";
import React, {FC} from "react";
import {FormikProps} from "formik";
import moment from "moment";
import {isDefined} from "../../../helpers/typeScriptHelpers";
import {CreateSepaXmlByTimeRangeInput} from "../../../generated/graphql";
import {createInputHelpers} from "../../../helpers/createInputHelpers";
import MoneyInput from "../../../views/MoneyInput/MoneyInput";
import {hasSepaPreviewPaymentCount} from "./sepaExportGuard";
import DateInput from "../../../views/DateInput/DateInput";

/**
 * Die Eingabefelder holen KEINE Daten mehr selbst (Issue #671). Sie melden nur noch ein
 * abgeschlossenes Eingabeereignis (Blur bzw. schliessender DatePicker) an den Preview-Koordinator
 * im Modal — dieser dedupliziert und serialisiert die Requests.
 */
type InputFieldsProps = {
    formikProps: FormikProps<CreateSepaXmlByTimeRangeInput>;
    onPreviewRequest: (values: CreateSepaXmlByTimeRangeInput) => void;
    style?: React.CSSProperties;
};

export const InputMaxAmount: FC<InputFieldsProps> = ({formikProps, onPreviewRequest, style}) => {
    const {values} = formikProps;
    const {handleNumberInputChange} = createInputHelpers(formikProps);

    return (
        <Form.Item
            style={{
                ...style,
                display: "flex",
                border: "1px solid lightgrey",
                borderRadius: "10px",
                padding: "7px",
            }}
            label="Anzahl begrenzen auf"
            name="maxAmount"
            help={
                <span style={{fontSize: "0.8em"}}>
                    {
                        "Optional. Ohne Angabe werden alle offenen Raten des Zeitraums exportiert (ohne Zeitraum höchstens 2000). Bei einer Grenze kannst du die verbleibenden Raten anschliessend in einer Folgedatei exportieren."
                    }
                </span>
            }
        >
            <Space>
                <InputNumber
                    style={{width: "100%"}}
                    value={isDefined(values.maxAmount) ? values.maxAmount : null}
                    onChange={(value) => {
                        handleNumberInputChange("maxAmount")(value ?? undefined);
                    }}
                    onBlur={() => {
                        onPreviewRequest(formikProps.values);
                    }}
                />
            </Space>
        </Form.Item>
    );
};

export const InputMaxMoneyAmount: FC<InputFieldsProps> = ({formikProps, onPreviewRequest, style}) => {
    const {values} = formikProps;
    const {handleNumberInputChange} = createInputHelpers(formikProps);

    return (
        <Form.Item
            style={{
                ...style,
                display: "flex",
                border: "1px solid lightgrey",
                borderRadius: "10px",
                padding: "7px",
            }}
            label="Betrag begrenzen auf"
            name="maxMoneyAmount"
            help={!isDefined(values.maxMoneyAmount) || values.maxMoneyAmount < 1 ? <span style={{fontSize: "0.8em"}}>{"Betrag von 0 wird ignoriert!"}</span> : undefined}
        >
            <Space>
                <MoneyInput
                    style={{
                        width: "100%",
                    }}
                    value={isDefined(values.maxMoneyAmount) ? values.maxMoneyAmount : null}
                    onChange={(value) => {
                        handleNumberInputChange("maxMoneyAmount")(value ?? undefined);
                    }}
                    onBlur={() => {
                        onPreviewRequest(formikProps.values);
                    }}
                />
            </Space>
        </Form.Item>
    );
};

export const InputCollectionDate: FC<InputFieldsProps> = ({formikProps, onPreviewRequest, style}) => {
    const {values} = formikProps;
    const {handleDatePickerChange, handleDatePickerBlur} = createInputHelpers(formikProps);

    return (
        <Form.Item
            label="Tag der Ausführung"
            style={{
                ...style,
                display: "flex",
                border: "1px solid lightgrey",
                borderRadius: "10px",
                padding: "7px",
            }}
        >
            <DateInput
                style={{width: "150px"}}
                onChange={handleDatePickerChange("collectionDate")}
                value={isDefined(values.collectionDate) ? moment(values.collectionDate) : undefined}
                displayFormat="DD.MM.YYYY"
                onOpenChange={(open) => {
                    handleDatePickerBlur("collectionDate")(open);
                    if (open === false) {
                        onPreviewRequest(formikProps.values);
                    }
                }}
                allowClear={true}
            />
        </Form.Item>
    );
};

export const InputStartEndRange: FC<InputFieldsProps> = ({formikProps, onPreviewRequest, style}) => {
    const {values} = formikProps;
    const {handleDatePickerChange, handleDatePickerBlur} = createInputHelpers(formikProps);

    return (
        <Form.Item
            label="Fälligkeitszeitraum"
            style={{
                ...style,
                display: "flex",
                border: "1px solid lightgrey",
                borderRadius: "10px",
                padding: "7px",
            }}
        >
            <Space size={5}>
                <Space>
                    <div>{"ab"}</div>
                    <DateInput
                        style={{
                            width: "100%",
                        }}
                        onChange={handleDatePickerChange("startDate")}
                        value={isDefined(values.startDate) ? moment(values.startDate) : undefined}
                        displayFormat="DD.MM.YYYY"
                        onOpenChange={(open) => {
                            handleDatePickerBlur("startDate")(open);
                            if (open === false) {
                                onPreviewRequest(formikProps.values);
                            }
                        }}
                        allowClear={true}
                    />
                    <div>{"bis"}</div>
                    <DateInput
                        style={{
                            width: "100%",
                        }}
                        onChange={handleDatePickerChange("endDate")}
                        value={isDefined(values.endDate) ? moment(values.endDate) : undefined}
                        displayFormat="DD.MM.YYYY"
                        onOpenChange={(open) => {
                            handleDatePickerBlur("endDate")(open);
                            if (open === false) {
                                onPreviewRequest(formikProps.values);
                            }
                        }}
                        allowClear={true}
                    />
                </Space>
            </Space>
        </Form.Item>
    );
};

// SUBMIT BUTTON -----------------------------------------------------------------------------------------------------------------

type CreateButtonProps = {
    noPayments: boolean;
    errors: Array<{errorTitle: string; errorMsg: string}> | undefined;
    /**
     * Schreibt die Datei (saveFile:true) und lädt sie herunter. Das zurückgegebene Promise wird an
     * `Modal.confirm.onOk` durchgereicht, damit AntD den OK-Button so lange im Loading-Zustand hält.
     */
    saveAndDownload: () => Promise<void>;
    paymentCount?: number;
    loading?: boolean;
};

export const InputCreateButton: FC<CreateButtonProps> = ({noPayments, errors, saveAndDownload, paymentCount, loading}) => {
    // Ohne erfolgreiche Vorschau ist paymentCount undefined — der Dialog zeigt dann
    // „Keine Zahlungen", der Server würde aber ohne Zeitraum ALLE offenen Raten
    // lastschriften und als bezahlt markieren (#880 entfernte die 2000er-Grenze,
    // #882 liess errors leer, damit die echte Vorschau ankommen kann).
    const hasPreviewCount = hasSepaPreviewPaymentCount(paymentCount);
    const exportBlocked = Boolean((errors && errors.length > 0) || noPayments || loading || !hasPreviewCount);

    return (
        <Tooltip title={!hasPreviewCount || noPayments ? "Es wurden keine exportierbaren Einträge gefunden" : null}>
            <Button
                type="primary"
                block
                loading={loading}
                // Doppelklick-Schutz: Während der Mutation läuft, keinen zweiten Export anstoßen.
                disabled={exportBlocked}
                onClick={() => {
                    if (!hasPreviewCount) {
                        return;
                    }

                    Modal.confirm({
                        style: {whiteSpace: "pre-line"},
                        title: `XML-Datei mit ${paymentCount} Posten`,
                        content: "Willst du diese jetzt erzeugen und die offenen Posten damit als bezahlt markieren?",
                        okText: "Jetzt erzeugen",
                        icon: null,

                        // Genau EINE Mutation pro bestätigtem Export (Issue #671): der frühere zusätzliche
                        // Vorschau-Aufruf (saveFile:false) entfällt. Promise zurückgeben ⇒ AntD wartet.
                        onOk: () => saveAndDownload(),
                    });
                }}
            >
                {"XML-Datei erstellen"}
            </Button>
        </Tooltip>
    );
};
